<template>
    <div>
        <PageHeader>
            {{ $t('widgeterReportsShops.title') }}
            <template #side>
                <div class="d-flex">
                    <MultiSelectField
                        v-model="pages"
                        :lazy="true"
                        variant="select"
                        class="width-250 mb-0"
                        :options="pagesMultiselectFormat"
                        :placeholder="$t('yourPages')"
                        :loading="isSpacesLoading"
                        :disabled="isCampaignsLoading"
                        :show-checkboxes="true"
                    >
                        <template slot="selection" slot-scope="{ values, isOpen }">
                            <span v-if="values.length && !isOpen" class="multiselect__single"> {{ $t('yourPages') }} ({{ values.length }}) </span>
                        </template>
                    </MultiSelectField>

                    <div class="mx-2"></div>

                    <MultiSelectField
                        v-model="shops"
                        :lazy="true"
                        variant="select"
                        class="width-250 mb-0"
                        :options="shopsMultiselectFormat"
                        :placeholder="$t('shops')"
                        :loading="isCampaignsLoading"
                        :show-checkboxes="true"
                    >
                        <template slot="selection" slot-scope="{ values, isOpen }">
                            <span v-if="values.length && !isOpen" class="multiselect__single"> {{ $t('shops') }} ({{ values.length }}) </span>
                        </template>
                    </MultiSelectField>

                    <div class="mx-2"></div>

                    <TimeRangeCalendar v-model="timeRange" :disabled="isCampaignsLoading" />
                </div>
            </template>
        </PageHeader>

        <div class="row">
            <div class="col mb-4 width-min-700 flex-grow-10">
                <MultiEChartCard
                    :data="lineChartData"
                    :height="400"
                    :loading="loading"
                    :title="$t('dashboard.overview')"
                    :group-by="groupBy"
                    @currentChart="setCurrentChart"
                />
            </div>

            <div class="col width-400 width-min-400 flex-grow-1">
                <EChart card :height="400" :loading="loading" :options="pieChartData[currentChart]" />
            </div>
        </div>

        <div class="row">
            <div class="col-xl-12 mb-4">
                <CampaignList :space-ids="pages || []" :campaign-ids="shops || []" :time-range="timeRange || {}" />
            </div>
        </div>
    </div>
</template>

<script>
    import cloneDeep from 'lodash/cloneDeep';
    import { generateEChartData, chartDataGroupBy } from '@/utils/chart';
    import PageHeader from '@/components/common/PageHeader.vue';
    import TimeRangeCalendar from '@/components/common/TimeRangeCalendar.vue';
    import MultiSelectField from '@/components/common/MultiSelectField.vue';
    import MultiEChartCard from '@/components/common/MultiEChartCard.vue';
    import EChart from '@/components/common/EChart.vue';
    import CampaignList from './parts/CampaignList.vue';

    export default {
        name: 'WidgeterReportsShops',
        components: {
            CampaignList,
            EChart,
            MultiEChartCard,
            MultiSelectField,
            PageHeader,
            TimeRangeCalendar,
        },

        data() {
            return {
                currentChart: 'offers_impressions',
                timeRange: null,
                pages: [],
                shops: [],
                isInit: false,
                lineChartData: {},
                pieChartData: {},
            };
        },

        computed: {
            defaultLineChartData() {
                const baseObject = {};
                const metricsOrder = ['offers_impressions', 'clicks', 'orders', 'amount', 'publisher_commission'];
                metricsOrder.forEach(metricName => {
                    baseObject[metricName] = {
                        series: [],
                        name: this.metricLabel(metricName),
                    };
                });
                return Object.freeze(baseObject);
            },
            defaultPieChartData() {
                return Object.freeze(cloneDeep(this.defaultLineChartData));
            },
            isSpacesLoading() {
                return this.$store.state.space.loading.fetchSpaces;
            },
            isCampaignsLoading() {
                return this.isSpacesLoading || this.$store.getters['campaigns/getLoading']('campaigns');
            },
            loading() {
                return this.isCampaignsLoading || this.$store.getters['userStats/getLoading']('campaignReport');
            },
            groupBy() {
                return chartDataGroupBy(this.timeRange) || 'day';
            },
            campaignReport() {
                return this.$store.getters['userStats/getCampaignReport'];
            },
            spaces() {
                return this.$store.state.space.spaces;
            },
            campaigns() {
                return this.$store.getters['campaigns/getCampaigns']?.items || [];
            },
            pagesMultiselectFormat() {
                return Object.values(this.spaces).map(({ id, name }) => ({ value: id, text: name }));
            },
            shopsMultiselectFormat() {
                return this.campaigns.map(({ id, name }) => ({ value: id, text: name }));
            },
        },

        watch: {
            timeRange(newValue, oldValue) {
                if (oldValue !== null) {
                    this.clearChartData();
                    this.fetchCampaignReport();
                }
            },
            currentChart() {
                const currentChartDataExist = this.lineChartData[this.currentChart]?.series?.length && this.pieChartData[this.currentChart]?.series?.length;
                if (!currentChartDataExist) {
                    this.fetchCampaignReport();
                }
            },
            campaignReport() {
                this.initChartData();
            },
            pages() {
                if (this.isInit) {
                    this.clearChartData();
                    this.fetchCampaigns();
                }
            },
            shops() {
                this.clearChartData();
                this.fetchCampaignReport();
            },
        },

        methods: {
            metricLabel(metricKey) {
                const map = {
                    offers_impressions: this.$t('widgeterReportsShops.impressions'),
                    clicks: this.$t('widgeterReportsShops.clicks'),
                    orders: this.$t('widgeterReportsShops.transactions'),
                    amount: this.$t('widgeterReportsShops.transactionAmount'),
                    publisher_commission: this.$t('widgeterReportsShops.commission'),
                };
                return map[metricKey] || metricKey;
            },

            clearChartData() {
                this.lineChartData = cloneDeep(this.defaultLineChartData);
                this.pieChartData = cloneDeep(this.defaultPieChartData);
            },

            async init() {
                this.clearChartData();
                await this.fetchSpaces();
                this.pages = Object.values(this.spaces).map(({ id }) => parseInt(id, 10));
                await this.fetchCampaigns();
                this.shops = this.campaigns.map(({ id }) => parseInt(id, 10));
                this.isInit = true;
                this.fetchCampaignReport();
            },

            fetchSpaces() {
                return this.$store.dispatch('space/fetchSpaces', { requestParams: { sort: 'name', order: 'ASC' } });
            },

            fetchCampaigns() {
                return this.$store.dispatch('campaigns/fetchCampaigns', {
                    requestParams: {
                        sort: 'name',
                        order: 'ASC',
                        active: true,
                        allowedFor: this.pages.join(','),
                    },
                });
            },

            fetchCampaignReport() {
                const requestParams = {
                    startDate: this.timeRange?.startDate,
                    endDate: this.timeRange?.endDate,
                    feature: this.currentChart,
                    groupBy: this.groupBy,
                    spaces: this.pages.join(','),
                    campaigns: this.shops.join(','),
                    showFirst: 9,
                };
                return this.$store.dispatch('userStats/fetchCampaignReport', { requestParams });
            },

            initChartData() {
                this.initLineChartData();
                this.initPieChartData();
            },

            initLineChartData() {
                if (this.campaignReport?.chart?.length) {
                    const seriesByMetric = {};
                    this.campaignReport.chart.forEach(row => {
                        Object.keys(row).forEach(fieldName => {
                            if (fieldName === 'date') return;
                            (seriesByMetric[fieldName] || (seriesByMetric[fieldName] = [])).push({
                                date: row.date,
                                value: row[fieldName],
                            });
                        });
                    });

                    const lineSeries = Object.keys(seriesByMetric).map(seriesName => {
                        const pointPairs = generateEChartData(this.timeRange, seriesByMetric[seriesName], this.groupBy);
                        const echartsData = pointPairs.map(point => [point.date, point.value]);
                        return { name: seriesName, data: echartsData };
                    });

                    const chartDataForCurrent = {
                        ...this.defaultLineChartData[this.currentChart],
                        series: lineSeries,
                        xAxis: { type: 'time' },
                        tooltip: {
                            valueFormatter: value => {
                                if (this.currentChart === 'clicks' || this.currentChart === 'orders') {
                                    return Math.round(value);
                                }
                                if (this.currentChart === 'amount' || this.currentChart === 'publisher_commission') {
                                    return this.$format.currency(value);
                                }
                                return value;
                            },
                        },
                    };

                    this.$set(this.lineChartData, this.currentChart, chartDataForCurrent);
                } else {
                    this.$set(this.lineChartData, this.currentChart, {
                        ...this.defaultLineChartData[this.currentChart],
                        series: [],
                    });
                }
            },

            initPieChartData() {
                const hasAnyValue = !!this.campaignReport?.pie_chart?.reduce((sum, row) => sum + (row?.[1] || 0), 0);
                const pieChartDataItems = hasAnyValue ? this.campaignReport.pie_chart.map(([label, totalValue]) => ({ name: label, value: Number(Number(totalValue).toFixed(2)) })) : [];
                const pieSeries = pieChartDataItems.length ? [{ type: 'pie', data: pieChartDataItems }] : [];
                this.$set(this.pieChartData[this.currentChart], 'series', pieSeries);
            },

            setCurrentChart(currentChart) {
                if (this.lineChartData[currentChart]) {
                    this.currentChart = currentChart;
                } else {
                    const keys = Object.keys(this.lineChartData);
                    this.currentChart = keys[parseInt(currentChart, 10)];
                }
            },

            setTimeRange(range) {
                this.timeRange = range;
            },
        },

        created() {
            this.init();
        },
    };
</script>
